import React, { useEffect, useState, useRef } from 'react';

/**
 * Custom hook to determine if an element is visible in the viewport
 * @param {Object} options - Intersection observer options
 * @returns {Array} - [ref, isVisible] where ref is attached to the target element
 */
export const useElementOnScreen = (options = {}) => {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Once isVisible is true, we don't want to change it back to false
  // This prevents the image from being unloaded if it scrolls out of view
  const [hasBecomeVisible, setHasBecomeVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      const isCurrentlyVisible = entry.isIntersecting;
      
      setIsVisible(isCurrentlyVisible);
      
      if (isCurrentlyVisible && !hasBecomeVisible) {
        setHasBecomeVisible(true);
      }
    }, options);

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [options, hasBecomeVisible]);

  // Return hasBecomeVisible as the visibility state to ensure we never "unload" an image
  return [elementRef, hasBecomeVisible || isVisible];
};

/**
 * LazyImage component that loads an image only when it's near the viewport
 * @param {Object} props - Component props
 * @returns {JSX.Element} - React component
 */
export const LazyImage = React.forwardRef(({
  src,
  alt,
  className = '',
  width,
  height,
  placeholderSrc = '',
  threshold = 0.1,
  style = {},
  ...props
}, ref) => {
  const [elementRef, isVisible] = useElementOnScreen({
    threshold,
    rootMargin: '100px',
  });

  const [imageSrc, setImageSrc] = useState(placeholderSrc || null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Add this effect to reset loading state when src changes
  useEffect(() => {
    // Reset loading state when the image source changes
    setIsLoaded(false);
    setImageSrc(placeholderSrc || null); // Reset to placeholder
  }, [src, placeholderSrc]); // Run when src or placeholderSrc changes
  
  // Load the image when the component becomes visible
  useEffect(() => {
    if (isVisible && !isLoaded) {
      const img = new Image();
      // Add cache-busting prevention - ensure we're using cached versions when available
      // by NOT adding timestamps or random values to URLs
      img.src = src;
      
      // Use an AbortController for better cleanup
      const abortController = new AbortController();
      
      img.onload = () => {
        if (!abortController.signal.aborted) {
          setImageSrc(src);
          setIsLoaded(true);
          
          // Store in sessionStorage to remember this image was loaded in this session
          try {
            sessionStorage.setItem(`img-loaded-${src}`, 'true');
          } catch (e) {
            // Ignore errors if sessionStorage is not available
          }
          
          // Forward the onLoad prop if provided
          if (props.onLoad) {
            props.onLoad();
          }
        }
      };
      img.onerror = () => {
        if (!abortController.signal.aborted) {
          console.error(`Failed to load image: ${src}`);
        }
      };
      
      // Check if we've loaded this image already in this session, but only for non-modal images
      // Modal images should always load to ensure proper zoom functionality
      try {
        if (!src.includes('modal-image')) {
          const wasLoaded = sessionStorage.getItem(`img-loaded-${src}`);
          if (wasLoaded === 'true') {
            // Skip the loading process and directly set as loaded
            setImageSrc(src);
            setIsLoaded(true);
          }
        }
      } catch (e) {
        // Ignore errors if sessionStorage is not available
      }
      
      return () => {
        abortController.abort();
      };
    }
  }, [isVisible, src, isLoaded]);

  return (
    <div
      ref={elementRef}
      className={`lazy-image-container ${className}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: width ? `${width}px` : 'auto',
        height: height ? `${height}px` : 'auto',
        display: 'inline-block',
        ...style,
      }}
    >
      {/* Show a placeholder or loading state until the image is loaded */}
      {!isLoaded && (
        <div
          className="lazy-image-placeholder"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {placeholderSrc ? (
            <img 
              src={placeholderSrc} 
              alt={`${alt} placeholder`}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            />
          ) : (
            <div
              style={{
                width: '30px',
                height: '30px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          )}
        </div>
      )}

      {/* Actual image that will be displayed once loaded */}
      {imageSrc && (
        <img
          ref={ref} // Forward the ref to the actual img element
          src={imageSrc}
          alt={alt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: props.style?.objectFit || 'cover',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            ...props.style // Merge with any custom styles
          }}
          loading="lazy"
          {...props}
          onLoad={(e) => {
            // Forward the event to the onLoad prop if provided
            if (props.onLoad) {
              props.onLoad(e);
            }
          }}
        />
      )}

      {/* Add a keyframes animation for the spinner */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

/**
 * Utility to generate a low-quality placeholder image
 * @param {string} src - Original image source
 * @param {number} width - Width of placeholder
 * @returns {Promise<string>} - Data URL of low-quality placeholder
 */
export const generatePlaceholderImage = (src, width = 50) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const aspectRatio = img.height / img.width;
      canvas.width = width;
      canvas.height = width * aspectRatio;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      try {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.1);
        resolve(dataUrl);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = (err) => {
      reject(err);
    };
    img.src = src;
  });
};

/**
 * Dynamic import utility for React components
 * @param {Function} importFunc - Import function
 * @returns {React.Component} - Lazy-loaded component
 */
export const lazyImport = (importFunc) => {
  return React.lazy(importFunc);
};
